/**
 * SchoolLetterTemplate.tsx
 *
 * Letter-template editor.  Mirrors SchoolLetterHeader.tsx almost exactly;
 * differences:
 *   - API endpoint  → /letterhead-templates  (POST / PUT)
 *   - Extra field   → template_type  (circular | notice | warning | permission | general)
 *   - No letterhead_id serial/date locked-blocks logic (templates are reusable blanks)
 *   - Toolbar title says "Template Composer"
 */

import React, { useState, useRef, useEffect } from "react";
import { type NavigateOptions } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import "./SchoolLetterHeader.css";
import apiClient from "../../stateManagers/services/axiosInterceptor";
import "primeicons/primeicons.css";
import LetterEditor from "./LetterEditor";
import type { CrudProps } from "../../router/routeWrappers";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";

import {
  PAGE_W,
  PAGE_H,
  SNAP,
  ZOOM_STEPS,
  FONT_SIZES,
  SCHOOL,
  makeEl as _makeEl,
  makeImgEl as _makeImgEl,
  buildPrint,
  type CanvasElement,
  type TextElement,
  type ImageElement,
  type ElementPatch,
  type LockedBlock,
} from "./SchoolLetterHeader";

// ── helpers re-exported from SchoolLetterHeader so we don't duplicate ──────────

function makeEl(o: Partial<TextElement> = {}): TextElement {
  const base: TextElement = {
    id: `el${Date.now()}`,
    type: "text",
    content: "Type here",
    html: "Type here",
    x: 80,
    y: 230,
    width: 420,
    fontSize: 13,
    fontFamily: "'DM Sans',sans-serif",
    color: "#000000",           // ← default black
    bold: false,
    italic: false,
    underline: false,
    align: "left",
  };
  const merged = { ...base, ...o };
  if (!merged.html) merged.html = merged.content;
  return merged;
}

function makeImgEl(o: Partial<ImageElement> = {}): ImageElement {
  return {
    id: `el${Date.now()}`,
    type: "image",
    content: "Signature",
    src: null,
    x: 80,
    y: 700,
    width: 180,
    height: 70,
    ...o,
  };
}

function buildPrintLocal(
  elements: CanvasElement[],
  headerSrc: string | null,
  footerSrc: string | null,
  hideHeader = false,
  hideFooter = false,
): string {
  const hTag = hideHeader
    ? ""
    : headerSrc
      ? `<img src="${headerSrc}" style="width:100%;display:block;" alt="header"/>`
      : `<div style="width:100%;height:160px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);"></div>`;

  const fTag = hideFooter
    ? ""
    : footerSrc
      ? `<img src="${footerSrc}" style="position:absolute;bottom:0;left:0;width:100%;display:block;" alt="footer"/>`
      : `<div style="position:absolute;bottom:0;left:0;width:100%;height:110px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);"></div>`;

  const elHTML = elements
    .map((el) => {
      if (el.type === "image") {
        if (!(el as ImageElement).src) return "";
        return `<div style="position:absolute;left:${el.x}px;top:${el.y}px;width:${el.width}px;height:${(el as ImageElement).height}px;z-index:2;"><img src="${(el as ImageElement).src}" style="width:100%;height:100%;object-fit:contain;" alt="sig"/></div>`;
      }
      const t = el as TextElement;
      return `<div style="position:absolute;left:${t.x}px;top:${t.y}px;width:${t.width}px;z-index:2;font-size:${t.fontSize}px;font-family:${t.fontFamily};color:${t.color};text-align:${t.align};line-height:1.6;white-space:pre-wrap;word-break:break-word;padding:4px 6px;">${t.html ?? t.content}</div>`;
    })
    .join("");

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,700;1,400&family=Playfair+Display:wght@700&family=Lato:wght@400;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet"/><style>* { margin: 0; padding: 0; box-sizing: border-box; } @page { size: A4; margin: 0; } body { width: 794px; min-height: 1123px; background: white; } .page { position: relative; width: 794px; min-height: 1123px; background: white; overflow: hidden; }</style></head><body><div class="page">${hTag}<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 2;">${elHTML}</div>${fTag}</div></body></html>`;
}

const TEMPLATE_TYPES = [
  { value: "general",    label: "General" },
  { value: "circular",   label: "Circular" },
  { value: "notice",     label: "Notice" },
  { value: "warning",    label: "Warning" },
  { value: "permission", label: "Permission" },
];

interface SchoolConfig {
  name: string;
  headerImage: string | null;
  footerImage: string | null;
}

export default function SchoolLetterTemplate({ id, onNavigate }: CrudProps) {
  const [elements, setEls]                   = useState<CanvasElement[]>([]);
  const [selId, setSelId]                    = useState<string | null>(null);
  const [toast, setToast]                    = useState("");
  const [printing, setPrinting]              = useState(false);
  const [templateTitle, setTemplateTitle]    = useState("New Template");
  const [templateType, setTemplateType]      = useState("general");
  const [zoom, setZoom]                      = useState(0.75);

  const [schoolConfig, setSchoolConfig]      = useState<SchoolConfig>({ name: "School", headerImage: null, footerImage: null });
  const [lockedBlocks, setLockedBlocks]      = useState<LockedBlock[]>([]);
  const [optionsLoading, setOptionsLoading]  = useState(true);
  const [isDirty, setIsDirty]                = useState(false);

  // ── Print-without-header/footer toggle ────────────────────────────────────
  const [hideHeader, setHideHeader] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);

  const [savedSnapshot, setSavedSnapshot]       = useState<string>("");
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const isDirtyRef       = useRef(false);
  const pendingNavRef    = useRef<string | number | null>(null);
  const elementsRef      = useRef<CanvasElement[]>(elements);
  const toastTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { elementsRef.current = elements; }, [elements]);

  useEffect(() => {
    const current = JSON.stringify({ title: templateTitle, type: templateType, elements });
    const dirty = current !== savedSnapshot;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, [elements, templateTitle, templateType, savedSnapshot]);

  // ── Load school config + existing template (edit mode) ─────────────────────
  useEffect(() => {
    setOptionsLoading(true);
    apiClient
      .get("/multiple-options?names=school_name,letterhead_header_image,letterhead_footer_image,letterhead_serial,letterhead_date")
      .then((res) => {
        if (res.data?.status === "success" && res.data?.data) {
          const d = res.data.data;
          const schoolName  = d.school_name?.data?.[0] || "School";
          const headerImage = d.letterhead_header_image?.data?.[0] || null;
          const footerImage = d.letterhead_footer_image?.data?.[0] || null;
          setSchoolConfig({ name: schoolName, headerImage, footerImage });

          const decodeEntities = (str: string): string => {
            const txt = document.createElement("textarea");
            txt.innerHTML = str;
            return txt.value;
          };

          const blocks: LockedBlock[] = [];
          const rawSerial = d.letterhead_serial?.data?.[0];
          if (rawSerial) {
            try {
              const c = JSON.parse(decodeEntities(rawSerial));
              blocks.push({ label: "S. No. :-", x: c.x, y: c.y, width: 440 });
            } catch {}
          } else {
            blocks.push({ label: "S. No. :-", x: 80, y: 170, width: 440 });
          }

          const rawDate = d.letterhead_date?.data?.[0];
          if (rawDate) {
            try {
              const c = JSON.parse(decodeEntities(rawDate));
              blocks.push({ label: "Date :-", x: c.x, y: c.y, width: c.width });
            } catch {}
          } else {
            blocks.push({ label: "Date :-", x: 500, y: 170, width: 200 });
          }

          setLockedBlocks(blocks);

          if (id) {
            loadTemplateForEdit(id, blocks, { name: schoolName, headerImage, footerImage });
          } else {
            setEls([]);
            setSelId(null);
            setTemplateTitle("New Template");
            setTemplateType("general");
            setSavedSnapshot(JSON.stringify({ title: "New Template", type: "general", elements: [] }));
            setIsDirty(false);
          }
        }
      })
      .catch(() => {
        const fallbackBlocks: LockedBlock[] = [
          { label: "S. No. :-", x: 80, y: 170, width: 440 },
          { label: "Date :-",   x: 500, y: 170, width: 200 },
        ];
        setLockedBlocks(fallbackBlocks);
        if (id) loadTemplateForEdit(id, fallbackBlocks, { name: "School", headerImage: null, footerImage: null });
        else {
          setEls([]);
          setSavedSnapshot(JSON.stringify({ title: "New Template", type: "general", elements: [] }));
        }
      })
      .finally(() => setOptionsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplateForEdit = async (templateId: number, fallbackBlocks: LockedBlock[], fallback: SchoolConfig) => {
    try {
      const res = await apiClient.get(`/letterhead-templates?template_id=${templateId}`);
      if (res.data.status === "success" && res.data.template) {
        const t = res.data.template;
        const title = t.title || "Edit Template";
        const type  = t.template_type || "general";

        const processedElements = (t.elements || []).map((el: any) => {
          if (el.type === "text" && !el.html) return { ...el, html: el.content || "" };
          return el;
        });

        setTemplateTitle(title);
        setTemplateType(type);
        setEls(processedElements);
        setSavedSnapshot(JSON.stringify({ title, type, elements: processedElements }));

        if (t.locked_blocks && t.locked_blocks.length > 0) {
          setLockedBlocks(t.locked_blocks.map((b: LockedBlock) => {
            const isSerial = b.label.toLowerCase().includes("s. no") || b.label.toLowerCase().includes("serial") || b.label.toLowerCase().includes("no.");
            const isDate   = b.label.toLowerCase().includes("date");
            if (isSerial) return { ...b, label: "S. No. :-", width: b.width ?? 440 };
            if (isDate)   return { ...b, label: "Date :-" };
            return b;
          }));
        } else {
          setLockedBlocks(fallbackBlocks);
        }
      }
    } catch {}
  };

  const updateLockedBlock = (index: number, x: number, y: number, width?: number) => {
    setLockedBlocks(prev =>
      prev.map((b, i) => i === index ? { ...b, x, y, ...(width !== undefined ? { width } : {}) } : b)
    );
  };

  // ── Navigation guard ────────────────────────────────────────────────────────
  const safeNavigate = (to: string | number, opts?: NavigateOptions) => {
    if (isDirtyRef.current) {
      pendingNavRef.current = to;
      setShowUnsavedModal(true);
    } else {
      onNavigate(to as any, opts);
    }
  };

  useEffect(() => {
    window.history.pushState({ _templateSentinel: true }, "");
    const onPopState = () => {
      if (!isDirtyRef.current) return;
      window.history.pushState({ _templateSentinel: true }, "");
      pendingNavRef.current = -1;
      setShowUnsavedModal(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const showToast = (m: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(m);
    toastTimerRef.current = setTimeout(() => setToast(""), 2400);
  };

  // ── Element helpers ─────────────────────────────────────────────────────────
  const addEl = (o: Partial<TextElement> = {}) => {
    const el = makeEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const addImgEl = (o: Partial<ImageElement> = {}) => {
    const el = makeImgEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const updateEl = (elId: string, patch: ElementPatch) => {
    setEls((p) => p.map((e) => (e.id === elId ? ({ ...e, ...patch } as CanvasElement) : e)));
  };

  const deleteEl = (elId: string) => {
    setEls((p) => p.filter((e) => e.id !== elId));
    if (selId === elId) setSelId(null);
  };

  const duplicateEl = (elId: string) => {
    const s = elementsRef.current.find((e) => e.id === elId);
    if (!s) return;
    if (s.type === "image") {
      const el = makeImgEl({ ...s, id: undefined as any, x: s.x + 14, y: s.y + 14 });
      setEls((p) => [...p, el]);
      setSelId(el.id);
    } else {
      addEl({ ...(s as TextElement), id: undefined as any, x: s.x + 14, y: s.y + 14 });
    }
  };

  // ── PDF helper ──────────────────────────────────────────────────────────────
  async function generatePdfFromHtml(html: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const element = document.createElement("div");
      element.innerHTML = html;
      document.body.appendChild(element);
      html2canvas(element, { scale: 2, logging: false, useCORS: true })
        .then((canvas) => {
          const pdf = new jsPDF("p", "mm", "a4");
          pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, 210, 297);
          const blob = pdf.output("blob");
          document.body.removeChild(element);
          resolve(blob);
        })
        .catch((err) => { document.body.removeChild(element); reject(err); });
    });
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  const performSave = async (): Promise<boolean> => {
    if (!templateTitle.trim()) { showToast("Please enter a title for this template"); return false; }
    if (elements.length === 0)  { showToast("Add at least one element before saving"); return false; }

    setPrinting(true);
    try {
      const printHTML = buildPrintLocal(
        elements,
        schoolConfig.headerImage,
        schoolConfig.footerImage,
        hideHeader,
        hideFooter,
      );
      await generatePdfFromHtml(printHTML);

      const formData = new FormData();
      formData.append("title",              templateTitle.trim());
      formData.append("template_type",      templateType);
      formData.append("elements_json",      JSON.stringify(elements));
      formData.append("locked_blocks_json", JSON.stringify(lockedBlocks));

      const response = id
        ? await apiClient.put(`/letterhead-templates/${id}`, formData)
        : await apiClient.post("/letterhead-templates", formData);

      if (response.data?.status === "success") {
        showToast(id ? "Template updated successfully" : "Template saved successfully");
        setSavedSnapshot(JSON.stringify({ title: templateTitle.trim(), type: templateType, elements }));

        if (!id) {
          const newId = response.data?.template?.id ?? response.data?.id;
          if (newId) onNavigate(`/letterhead-templates/edit/${newId}`, { replace: true });
        }
        return true;
      }

      showToast("Save failed");
      return false;
    } catch (error: any) {
      showToast(error.response?.data?.message || "Failed to save. Please try again.");
      return false;
    } finally {
      setPrinting(false);
    }
  };

  const handleSaveOnly = async () => { await performSave(); };
  const handleSaveAndPrint = handleSaveOnly;
  const handlePrintOnly    = () => {};
  const handleNewTemplate  = () => safeNavigate("/letterhead-templates/new");

  const handleModalKeepEditing = () => { pendingNavRef.current = null; setShowUnsavedModal(false); };

  const handleModalDiscard = () => {
    setSavedSnapshot(JSON.stringify({ title: templateTitle, type: templateType, elements }));
    isDirtyRef.current = false;
    setIsDirty(false);
    const to = pendingNavRef.current;
    pendingNavRef.current = null;
    setShowUnsavedModal(false);
    if (to !== null) onNavigate(to as any);
  };

  const handleModalSave = async () => {
    const saved = await performSave();
    if (saved) {
      const to = pendingNavRef.current;
      pendingNavRef.current = null;
      setShowUnsavedModal(false);
      if (to !== null) onNavigate(to as any);
    }
  };

  if (optionsLoading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", flexDirection: "column", gap: 12,
        background: "#f8faff",
      }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: 32, color: "#2855ae" }} />
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>Loading school configuration…</p>
      </div>
    );
  }

  return (
    <>
      {/* ── Type selector strip above editor ── */}
      <div style={{
        background: "white",
        borderBottom: "1px solid #e5e7eb",
        padding: "6px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "'DM Sans',sans-serif",
        fontSize: 12,
      }}>
        <span style={{ color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>
          Template Type:
        </span>
        {TEMPLATE_TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => setTemplateType(t.value)}
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              border: "1px solid",
              borderColor: templateType === t.value ? "#1e3a8a" : "#e5e7eb",
              background: templateType === t.value
                ? "linear-gradient(135deg,#1e3a8a,#3b82f6)"
                : "transparent",
              color: templateType === t.value ? "white" : "#374151",
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <LetterEditor
        elements={elements}
        selId={selId}
        zoom={zoom}
        letterheadTitle={templateTitle}
        printing={printing}
        onTitleChange={setTemplateTitle}
        onAddEl={addEl}
        onAddImgEl={addImgEl}
        onUpdateEl={updateEl}
        onDeleteEl={deleteEl}
        onDuplicateEl={duplicateEl}
        onSaveAndPrint={handleSaveAndPrint}
        onSetSelId={setSelId}
        SCHOOL={{
          ...SCHOOL,
          name: schoolConfig.name,
          headerImage: schoolConfig.headerImage ?? "",
          footerImage: schoolConfig.footerImage ?? "",
        }}
        PAGE_W={PAGE_W}
        PAGE_H={PAGE_H}
        SNAP={SNAP}
        FONT_SIZES={FONT_SIZES}
        isEditing={!!id}
        onNewLetterhead={handleNewTemplate}
        lockedBlocks={lockedBlocks}
        onUpdateLockedBlock={updateLockedBlock}
        currentLetterheadId=""
        isDirty={isDirty}
        onPrintOnly={handlePrintOnly}
        titleBarLabel="Template Composer"
        isTemplateMode={true}
        onSaveOnly={handleSaveOnly}
        onZoomChange={setZoom}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
        onHideHeaderChange={setHideHeader}
        onHideFooterChange={setHideFooter}
      />

      <UnsavedChangesModal
        open={showUnsavedModal}
        onClose={handleModalKeepEditing}
        onDiscard={handleModalDiscard}
        onSave={handleModalSave}
        saving={printing}
      />

      {toast && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "#1e3a8a", color: "white", padding: "10px 22px",
          borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 99999,
          boxShadow: "0 4px 18px rgba(30,58,138,0.3)",
        }}>
          {toast}
        </div>
      )}
    </>
  );
}